"use client";
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { captureEvent } from "@/src/helpers/trackEvent";

/* Lightweight mobile detector. Used to branch structural layouts (e.g. a
 * parallax desk on desktop vs a static scene on mobile). For pure visual
 * rebalancing, prefer CSS media queries in landing-v6.css.
 * SSR-safe: returns false on first render so markup matches, then re-runs
 * on mount to swap to the mobile variant if applicable.
 */
export function useIsMobile(breakpoint = 760) {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = () => setMobile(mq.matches);
    handler();
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [breakpoint]);
  return mobile;
}

const POSES = {
  reading: [0, 0],
  waving: [1, 0],
  teaching: [2, 0],
  laptop: [0, 1],
  thinking: [1, 1],
  sleeping: [2, 1],
};
const SPRITE = "/landing-v6/sprite_sheet.png";

/* Pose-specific micro-animations — cleverer than a generic up-down bob. */
const POSE_ANIM = {
  reading:  "qv6-pose-read 4.8s ease-in-out infinite",       // gentle page nod
  waving:   "qv6-pose-wave 2.2s ease-in-out infinite",       // hand wave
  teaching: "qv6-pose-teach 3.6s ease-in-out infinite",      // small chalk-tap
  laptop:   "qv6-pose-type 1.8s steps(2,end) infinite",      // typing jitter
  thinking: "qv6-pose-think 5.4s ease-in-out infinite",      // slow head tilt
  sleeping: "qv6-pose-sleep 6s ease-in-out infinite",        // long slow breath
};

export function Mascot({ pose = "reading", size = 160, float = true, style = {}, zzz }) {
  const [col, row] = POSES[pose] || POSES.reading;
  const bg = size * 3;
  const animation = float ? POSE_ANIM[pose] || POSE_ANIM.reading : "none";
  const showZzz = zzz !== undefined ? zzz : (pose === "sleeping" && float);
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        ...style,
      }}
      role="img"
      aria-label={`quarkle ${pose}`}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundImage: `url(${SPRITE})`,
          backgroundSize: `${bg}px ${(bg * 2) / 3}px`,
          backgroundPosition: `-${col * size}px -${row * size}px`,
          backgroundRepeat: "no-repeat",
          animation,
          transformOrigin: "60% 80%",
          overflow: "hidden",
        }}
      />
      {showZzz && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: size * 0.04,
            right: size * 0.02,
            fontFamily: "'Gambarino', serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: size * 0.18,
            lineHeight: 1,
            color: "var(--pink)",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <span style={{ display: "inline-block", animation: "qv6-zzz-a 3s ease-out infinite" }}>Z</span>
          <span style={{ display: "inline-block", animation: "qv6-zzz-b 3s ease-out infinite", marginLeft: 2, fontSize: "0.8em" }}>z</span>
          <span style={{ display: "inline-block", animation: "qv6-zzz-c 3s ease-out infinite", marginLeft: 1, fontSize: "0.62em" }}>z</span>
        </span>
      )}
    </div>
  );
}

/* Direction 1 wordmark: Gambarino, roman with "uar" set in italic */
export function Wordmark({ size = 40, color = "var(--ink)" }) {
  return (
    <span
      style={{
        fontFamily: "'Gambarino', 'DM Serif Display', serif",
        fontSize: size,
        color,
        lineHeight: 0.9,
        letterSpacing: "-0.04em",
        fontWeight: 400,
        display: "inline-block",
      }}
    >
      Q<span style={{ fontStyle: "italic" }}>uar</span>kle
    </span>
  );
}

/* A simple, saturated "planet" — used wherever the editor would show Quarkle's reply avatar.
 * Stroke-based, currentColor-driven, consistent with the editor pattern.
 */
export function QuarklePlanet({ size = 40, color = "var(--pink)", ring = "var(--ink)" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: "block", flexShrink: 0 }}>
      <defs>
        <clipPath id={`qv6-planet-clip-${size}`}>
          <circle cx="24" cy="24" r="14" />
        </clipPath>
      </defs>
      <ellipse cx="24" cy="24" rx="22" ry="7" fill="none" stroke={ring} strokeWidth="1.4" opacity="0.75" transform="rotate(-18 24 24)" />
      <circle cx="24" cy="24" r="14" fill={color} stroke={ring} strokeWidth="1.6" />
      <g clipPath={`url(#qv6-planet-clip-${size})`}>
        <circle cx="18" cy="20" r="3" fill={ring} opacity="0.18" />
        <circle cx="30" cy="28" r="4" fill={ring} opacity="0.14" />
        <path d="M10 28 Q24 22 38 30" fill="none" stroke={ring} strokeWidth="1.2" opacity="0.35" />
      </g>
      <ellipse cx="24" cy="24" rx="22" ry="7" fill="none" stroke={ring} strokeWidth="1.4" opacity="0.35" transform="rotate(-18 24 24)" strokeDasharray="2 4" />
    </svg>
  );
}

/* Direction 1 mark: trio of orbs compressed into a single 84×84 glyph */
export function QuarkleLogo({ size = 84, color = "currentColor", style = {} }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 84 84"
      fill="none"
      style={{ display: "block", color, ...style }}
      aria-label="Quarkle"
      role="img"
    >
      {/* small orb top-right */}
      <circle cx="62" cy="18" r="10" stroke="currentColor" strokeWidth="3" />
      {/* striped orb top-left */}
      <circle cx="20" cy="24" r="14" stroke="currentColor" strokeWidth="3" />
      <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="8" y1="27" x2="24" y2="27" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      {/* big banded orb bottom */}
      <circle cx="46" cy="58" r="22" stroke="currentColor" strokeWidth="3" />
      <path d="M27 52 Q37 47 47 52 T67 54" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M25 62 Q37 57 47 62 T69 62" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M28 70 Q38 66 48 70" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export function Button({ children, variant = "primary", size = "md", style = {}, ...rest }) {
  const sizes = {
    md: { padding: "14px 22px", fontSize: 15 },
    lg: { padding: "18px 28px", fontSize: 16 },
    sm: { padding: "8px 14px", fontSize: 13 },
    xl: { padding: "22px 34px", fontSize: 18 },
  };
  const variants = {
    primary: { background: "var(--ink)", color: "var(--paper)", border: "1px solid var(--ink)" },
    pink: { background: "var(--pink)", color: "#fff", border: "1px solid var(--ink)", boxShadow: "4px 4px 0 var(--ink)" },
    ghost: { background: "transparent", color: "var(--ink)", border: "1px solid var(--ink)" },
    subtle: { background: "transparent", color: "var(--ink)", border: "1px solid var(--rule)" },
    lime: { background: "var(--lime)", color: "#1A1410", border: "1px solid #1A1410", boxShadow: "4px 4px 0 #1A1410" },
  };
  return (
    <button
      {...rest}
      style={{
        ...variants[variant],
        ...sizes[size],
        borderRadius: 999,
        cursor: "pointer",
        fontFamily: "inherit",
        fontWeight: 500,
        letterSpacing: "0.01em",
        transition: "transform 0.15s ease, background 0.2s ease, box-shadow 0.15s ease",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        whiteSpace: "nowrap",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translate(-2px, -2px)";
        if (variant === "pink" || variant === "lime") {
          e.currentTarget.style.boxShadow = variant === "lime" ? "6px 6px 0 #1A1410" : "6px 6px 0 var(--ink)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translate(0, 0)";
        if (variant === "pink" || variant === "lime") {
          e.currentTarget.style.boxShadow = variant === "lime" ? "4px 4px 0 #1A1410" : "4px 4px 0 var(--ink)";
        }
      }}
    >
      {children}
    </button>
  );
}

export function StartWritingButton({ children = "start writing →", variant = "pink", size = "xl", style = {} }) {
  const { loginWithRedirect } = useAuth0();
  const onClick = () => {
    captureEvent("start_writing_clicked");
    loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
  };
  return (
    <Button onClick={onClick} variant={variant} size={size} style={style}>
      {children}
    </Button>
  );
}

export function LoginButton({ children = "log in", variant = "subtle", size = "sm", style = {} }) {
  const { loginWithRedirect } = useAuth0();
  const onClick = () => {
    captureEvent("landing_login_clicked");
    loginWithRedirect();
  };
  return (
    <Button onClick={onClick} variant={variant} size={size} style={style}>
      {children}
    </Button>
  );
}

/* YouTube modal player — inline popup, no redirect. */
const DEMO_YOUTUBE_ID = "BL4QaPuvmsI";
export function YouTubeModal({ open, onClose, videoId = DEMO_YOUTUBE_ID, title = "Quarkle · 60-second demo" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(10, 8, 14, 0.82)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "qv6-suggest-in 0.25s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "min(1100px, 96vw)",
          aspectRatio: "16 / 9",
          background: "var(--ink)",
          border: "1.5px solid var(--paper)",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 40px 120px -20px rgba(0,0,0,0.7), 12px 12px 0 var(--pink)",
        }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ width: "100%", height: "100%", border: 0 }}
        />
        <button
          onClick={onClose}
          aria-label="close video"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            width: 36,
            height: 36,
            borderRadius: 999,
            background: "var(--paper)",
            color: "var(--ink)",
            border: "1.5px solid var(--ink)",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 14,
            cursor: "pointer",
            boxShadow: "2px 2px 0 var(--ink)",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/* Volume nº is year - 2023; issue nº is ISO week. Day-of-year, month-and-ordinal for the masthead. */
export function formatMasthead(now = new Date()) {
  const months = ["january","february","march","april","may","june","july","august","september","october","november","december"];
  const days = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  const d = now.getDate();
  const dayName = days[now.getDay()];
  const monthName = months[now.getMonth()];
  const year = now.getFullYear();
  const vol = year - 2023; // "vol. iii" for 2026
  const romans = ["","i","ii","iii","iv","v","vi","vii","viii","ix","x","xi","xii","xiii","xiv","xv","xvi"];
  /* ISO week number */
  const tmp = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  const ord = (n) => {
    const s = ["th","st","nd","rd"], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const hour = now.getHours();
  const timeOfDay =
    hour < 5 ? "the quiet hour before dawn"
    : hour < 12 ? "a fresh kind of morning"
    : hour < 17 ? "a soft kind of afternoon"
    : hour < 21 ? "a lamplit evening"
    : "a late, stubborn hour";
  return {
    left: `vol. ${romans[vol] || vol} · no. ${weekNo} · ${dayName}, the ${ord(d)} of ${monthName}`,
    right: `ink on paper · also, software · ${timeOfDay}`,
  };
}

const NAV_LINKS = [
  ["Features", "/features"],
  ["Pro", "/pro"],
  ["Pricing", "/pricing"],
  ["Stories", "/blog"],
];

export function Nav() {
  const [mast, setMast] = useState(() => formatMasthead());
  useEffect(() => {
    setMast(formatMasthead()); // re-run on client in case server/client drift
    const t = setInterval(() => setMast(formatMasthead()), 60_000);
    return () => clearInterval(t);
  }, []);
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        backdropFilter: "blur(14px)",
        background: "color-mix(in oklab, var(--paper) 82%, transparent)",
        borderBottom: "1px solid var(--ink)",
      }}
    >
      <div
        className="qv6-nav-masthead"
        style={{
          borderBottom: "1px dashed var(--rule)",
          padding: "4px 40px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span className="mono" style={{ fontSize: 10, opacity: 0.65 }}>
          {mast.left}
        </span>
        <span className="mono" style={{ fontSize: 10, opacity: 0.65 }}>
          {mast.right}
        </span>
      </div>
      <div
        className="qv6-nav-row"
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          padding: "14px 40px",
          gap: 24,
        }}
      >
        <a href="/" className="qv6-nav-logo" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <QuarkleLogo size={42} color="var(--ink)" />
          <span className="qv6-wordmark"><Wordmark size={34} /></span>
          <span className="mono qv6-nav-logo-sub" style={{ fontSize: 10, opacity: 0.5, marginLeft: 4 }}>
            / a writing co.
          </span>
        </a>
        <nav className="qv6-nav-links" style={{ display: "flex", gap: 30, justifyContent: "center", alignItems: "center" }}>
          {NAV_LINKS.map(([l, href, opts], i) => {
            const accent = opts && opts.accent;
            return (
              <a
                key={i}
                href={href}
                title={opts && opts.title}
                style={{
                  color: accent ? "var(--pink)" : "var(--ink)",
                  textDecoration: "none",
                  fontSize: accent ? 16 : 14,
                  opacity: accent ? 1 : 0.8,
                  fontWeight: 500,
                  fontFamily: "'Gambarino', serif",
                  fontStyle: "italic",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  letterSpacing: accent ? "0.02em" : undefined,
                }}
              >
                {accent && (
                  <span
                    aria-hidden
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 999,
                      background: "var(--pink)",
                      animation: "qv6-qpulse 1.4s infinite",
                    }}
                  />
                )}
                {l}
              </a>
            );
          })}
        </nav>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a
            href="https://discord.gg/cPfBDmZNM2"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Join our Discord"
            title="Join our Discord"
            className="qv6-nav-discord"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 999,
              border: "1px solid var(--ink)",
              color: "var(--ink)",
              background: "transparent",
              textDecoration: "none",
              transition: "transform 0.15s, background 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--ink)";
              e.currentTarget.style.color = "var(--paper)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--ink)";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.249.077.077 0 0 0-.079-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.579.099 18.057a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.372.292a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.106c.36.699.772 1.364 1.226 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.673-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.177 1.095 2.157 2.419 0 1.334-.955 2.419-2.157 2.419zm7.975 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.095 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z" />
            </svg>
          </a>
          <span className="qv6-nav-cta-desktop"><LoginButton /></span>
          <StartWritingButton variant="pink" size="sm" style={{ boxShadow: "3px 3px 0 var(--ink)" }}>
            start writing →
          </StartWritingButton>
        </div>
      </div>
    </header>
  );
}

export function Marquee({ items, bg = "var(--ink)", fg = "var(--paper)", pink = "var(--pink)" }) {
  const content = [...items, ...items, ...items];
  return (
    <div
      className="qv6-marquee"
      style={{
        overflow: "hidden",
        borderTop: "2px solid var(--ink)",
        borderBottom: "2px solid var(--ink)",
        background: bg,
        color: fg,
        position: "relative",
      }}
    >
      <div
        className="qv6-marquee-content"
        style={{
          display: "flex",
          gap: 44,
          whiteSpace: "nowrap",
          animation: "qv6-marq 60s linear infinite",
          padding: "22px 0",
          width: "max-content",
        }}
      >
        {content.map((it, i) => (
          <span
            key={i}
            className="display"
            style={{
              fontSize: 44,
              fontStyle: "italic",
              display: "inline-flex",
              alignItems: "center",
              gap: 44,
            }}
          >
            {it}
            <span aria-hidden style={{ display: "inline-block" }}>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  d="M12 2l2.9 6.6L22 9.3l-5.2 4.7L18.3 21 12 17.3 5.7 21 7.2 14 2 9.3l7.1-.7L12 2z"
                  fill={pink}
                />
              </svg>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
